/**
 * CategoryFilters - Filter components for CategoryPage
 * Extracted for better modularity
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { 
  Home, ChevronRight, ChevronDown, X, SlidersHorizontal 
} from 'lucide-react';
import CityAutocomplete from '../CityAutocomplete';

/**
 * Breadcrumbs Component
 */
export function Breadcrumbs({ slug, getCategoryNameFromSlug }) {
  return (
    <nav className="flex items-center text-sm text-gray-500 flex-wrap" data-testid="category-breadcrumb">
      <Link to="/" className="hover:text-gray-900 flex items-center gap-1 transition-colors">
        <Home className="h-4 w-4" />
        <span>Home</span>
      </Link>

      {slug && slug !== 'all' && (
        <>
          <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
          <Link to="/category/all" className="hover:text-gray-900 transition-colors">
            All Categories
          </Link>
          <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
          <span className="text-gray-900 font-medium">{getCategoryNameFromSlug(slug)}</span>
        </>
      )}

      {slug === 'all' && (
        <>
          <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
          <span className="text-gray-900 font-medium">All Categories</span>
        </>
      )}
    </nav>
  );
}

/**
 * Category Filter with Subcategories
 */
export function CategoryFilter({
  categories,
  slug,
  selectedCategory,
  selectedSubcategory,
  expandedCategories,
  onCategoryClick,
  onToggleExpand
}) {
  return (
    <div className="space-y-2">
      <Label className="mb-2 block font-semibold">Categories</Label>
      
      {/* All Categories option */}
      <div
        onClick={() => onCategoryClick('all')}
        className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
          !selectedCategory && slug === 'all' 
            ? 'bg-blue-100 border-2 border-blue-500' 
            : 'hover:bg-gray-100 border-2 border-transparent'
        }`}
      >
        <span className="font-medium">All Categories</span>
      </div>
      
      {/* Categories List */}
      <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-1">
        {categories.map((category) => (
          <div key={category.id}>
            <div
              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                selectedCategory === category.slug && !selectedSubcategory
                  ? 'bg-blue-100 border-2 border-blue-500' 
                  : 'hover:bg-gray-100 border-2 border-transparent'
              }`}
            >
              <div 
                className="flex items-center gap-2 flex-1"
                onClick={() => onCategoryClick(category.slug)}
              >
                <span className="text-lg">{category.icon}</span>
                <span className="text-sm font-medium">{category.name}</span>
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="text-xs">
                  {category.ad_count || 0}
                </Badge>
                {category.subcategories?.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExpand(category.slug);
                    }}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    {expandedCategories[category.slug] ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {/* Subcategories */}
            {category.subcategories?.length > 0 && expandedCategories[category.slug] && (
              <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
                {category.subcategories.map((sub) => (
                  <div
                    key={sub.id}
                    onClick={() => onCategoryClick(sub.slug, true)}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer transition-all ${
                      selectedSubcategory === sub.slug || slug === sub.slug
                        ? 'bg-blue-100 border-l-2 border-blue-500' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-sm text-gray-700">{sub.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {sub.ad_count || 0}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Filter Section Component
 */
export function FilterSection({
  categories,
  categoriesLoading,
  slug,
  selectedCategory,
  selectedSubcategory,
  selectedLocation,
  condition,
  priceRange,
  expandedCategories,
  countryConfig,
  formatCurrency,
  onCategoryClick,
  onToggleExpand,
  onLocationChange,
  onConditionChange,
  onPriceRangeChange,
  onClearFilters
}) {
  return (
    <div className="space-y-6">
      {/* Categories with Subcategories */}
      {!categoriesLoading && (
        <CategoryFilter
          categories={categories}
          slug={slug}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          expandedCategories={expandedCategories}
          onCategoryClick={onCategoryClick}
          onToggleExpand={onToggleExpand}
        />
      )}
      
      <div className="border-t pt-4">
        <Label className="mb-2 block">Location</Label>
        <CityAutocomplete
          value={selectedLocation}
          onChange={onLocationChange}
          placeholder="Type nearest city"
        />
      </div>

      <div>
        <Label className="mb-2 block">Condition</Label>
        <Select value={condition} onValueChange={onConditionChange}>
          <SelectTrigger>
            <SelectValue placeholder="All Conditions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Conditions</SelectItem>
            <SelectItem value="Brand New">Brand New</SelectItem>
            <SelectItem value="Like New">Like New</SelectItem>
            <SelectItem value="Used">Used</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-3 block">Price Range ({countryConfig?.currency_symbol || 'Rs.'})</Label>
        <Slider
          value={priceRange}
          onValueChange={onPriceRangeChange}
          max={10000000}
          step={100000}
          className="mb-2"
        />
        <div className="flex justify-between text-sm text-gray-500">
          <span>{formatCurrency(priceRange[0])}</span>
          <span>{formatCurrency(priceRange[1])}</span>
        </div>
      </div>

      <Button variant="outline" className="w-full" onClick={onClearFilters}>
        <X className="h-4 w-4 mr-2" />
        Reset Filters
      </Button>
    </div>
  );
}

/**
 * Active Filters Display
 */
export function ActiveFilters({
  currentCategory,
  selectedLocation,
  condition,
  onNavigate,
  onLocationClear,
  onConditionClear,
  onClearAll
}) {
  const filters = [];
  
  if (currentCategory) {
    filters.push({
      label: currentCategory.name,
      onRemove: () => onNavigate('/category/all')
    });
  }
  
  if (selectedLocation) {
    filters.push({
      label: `Location: ${selectedLocation}`,
      onRemove: onLocationClear
    });
  }
  
  if (condition !== 'all') {
    filters.push({
      label: `Condition: ${condition}`,
      onRemove: onConditionClear
    });
  }
  
  if (filters.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {filters.map((filter, index) => (
        <Badge 
          key={index} 
          variant="secondary" 
          className="flex items-center gap-1 px-3 py-1"
        >
          {filter.label}
          <button onClick={filter.onRemove} className="ml-1 hover:text-red-500">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <Button variant="ghost" size="sm" onClick={onClearAll} className="text-xs">
        Clear All
      </Button>
    </div>
  );
}

/**
 * Sidebar Filter Card
 */
export function SidebarFilterCard({ children }) {
  return (
    <Card className="p-4 sticky top-20">
      <div className="flex items-center space-x-2 mb-4">
        <SlidersHorizontal className="h-5 w-5 text-blue-600" />
        <h2 className="text-lg font-semibold">Filters</h2>
      </div>
      {children}
    </Card>
  );
}
